import axios from 'axios';
import { supabase } from '../lib/supabaseClient';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const ORG_ID_KEY = 'divispend_biz_org_id';

// Dedicated axios instance for the Business Workspace. It NEVER attaches the
// legacy consumer JWT (divispend_access_token) — only a live Supabase access
// token + the active org id. This keeps the two auth boundaries from ever
// mixing on the wire.
export const bizApi = axios.create({ baseURL: API });

bizApi.interceptors.request.use(async (config) => {
  if (!supabase) return config;
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const orgId = localStorage.getItem(ORG_ID_KEY);
  if (orgId) config.headers['x-org-id'] = orgId;
  return config;
});

let onSessionExpired = null;
let onStepUpRequired = null;
export function registerBizApiHandlers({ sessionExpired, stepUpRequired }) {
  onSessionExpired = sessionExpired;
  onStepUpRequired = stepUpRequired;
}

bizApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const wwwAuth = (error.response?.headers?.['www-authenticate'] || '').toLowerCase();
    if (status === 401 && onSessionExpired) onSessionExpired();
    if (status === 403 && wwwAuth.includes('insufficient_aal') && onStepUpRequired) onStepUpRequired();
    return Promise.reject(error);
  }
);

// Organizations / memberships
export const orgsAPI = {
  mine: () => bizApi.get('/orgs/mine'),
  create: (data) => bizApi.post('/orgs', data),
  get: (orgId) => bizApi.get(`/orgs/${orgId}`),
  update: (orgId, data) => bizApi.patch(`/orgs/${orgId}`, data),
  transferOwnership: (orgId, newOwnerUserId) =>
    bizApi.post(`/orgs/${orgId}/transfer-ownership`, null, { params: { new_owner_user_id: newOwnerUserId } }),
  members: (orgId) => bizApi.get(`/orgs/${orgId}/members`),
  invite: (orgId, data) => bizApi.post(`/orgs/${orgId}/invitations`, data),
  removeMember: (orgId, userId) => bizApi.delete(`/orgs/${orgId}/members/${userId}`),
};

// Business Workspace (Stripe Connect dashboard — sandbox only in this phase)
export const bizWorkspaceAPI = {
  connectOnboard: (returnUrl) => bizApi.post('/biz/connect/onboard', { return_url: returnUrl, refresh_url: returnUrl }),
  connectStatus: () => bizApi.get('/biz/connect/status'),
  dashboard: () => bizApi.get('/biz/dashboard'),
  createInvoice: (data) => bizApi.post('/biz/invoices', data),
  listInvoices: () => bizApi.get('/biz/invoices'),
  addCollaborator: (data) => bizApi.post('/biz/collaborators', data),
  listCollaborators: () => bizApi.get('/biz/collaborators'),
  collabLink: (id) => bizApi.post(`/biz/collaborators/${id}/onboard-link`),
  createSplit: (data) => bizApi.post('/biz/splits', data),
  listSplits: () => bizApi.get('/biz/splits'),
  splitStatus: (id) => bizApi.get(`/biz/splits/${id}/status`),
  createPledge: (data) => bizApi.post('/biz/pledges', data),
  listPledges: () => bizApi.get('/biz/pledges'),
};

// Business Workspace records
export const bizRecordsAPI = {
  listContacts: (params) => bizApi.get('/biz/contacts', { params }),
  createContact: (data) => bizApi.post('/biz/contacts', data),
  updateContact: (id, data) => bizApi.patch(`/biz/contacts/${id}`, data),
  deleteContact: (id) => bizApi.delete(`/biz/contacts/${id}`),

  listReceipts: (params) => bizApi.get('/biz/receipts', { params }),
  createReceipt: (formData) => bizApi.post('/biz/receipts', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateReceipt: (id, data) => bizApi.patch(`/biz/receipts/${id}`, data),
  deleteReceipt: (id) => bizApi.delete(`/biz/receipts/${id}`),
  getReceiptFile: (id) => bizApi.get(`/biz/receipts/${id}/file`, { responseType: 'blob' }),

  listTransactions: (params) => bizApi.get('/biz/transactions', { params }),
  createTransaction: (data) => bizApi.post('/biz/transactions', data),
  updateTransaction: (id, data) => bizApi.patch(`/biz/transactions/${id}`, data),
  deleteTransaction: (id) => bizApi.delete(`/biz/transactions/${id}`),

  listWorkspaceInvoices: (params) => bizApi.get('/biz/workspace-invoices', { params }),
  getWorkspaceInvoice: (id) => bizApi.get(`/biz/workspace-invoices/${id}`),
  createWorkspaceInvoice: (data) => bizApi.post('/biz/workspace-invoices', data),
  updateWorkspaceInvoice: (id, data) => bizApi.patch(`/biz/workspace-invoices/${id}`, data),
  deleteWorkspaceInvoice: (id) => bizApi.delete(`/biz/workspace-invoices/${id}`),
};
